import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
// import Aura from '@primeng/themes/aura';
import {CustomTheme} from './custom-theme';
import {
    provideRouter,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
} from '@angular/router';
import { routes } from './app/app.routes';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    withInterceptorsFromDi,
    provideHttpClient,
} from '@angular/common/http';
import { InterceptorService } from './app/services/interceptor.service';
import { MessageService } from 'primeng/api';
import { AuthService } from './app/services/auth.service';
import { AuthGuard } from './app/core/guards/auth.guard';
import {
    CommonModule,
    DatePipe,
    HashLocationStrategy,
    LocationStrategy,
} from '@angular/common';
import { PrimengImportsModule } from './app/shared/primeng-imports/primeng-imports.module';
import {
    NgxUiLoaderConfig,
    NgxUiLoaderModule,
    NgxUiLoaderService,
} from 'ngx-ui-loader';
import { RouteGuard } from './app/core/guards/route.guard';
import { MessageManagerService } from './app/services/messagemanagerService.service';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { portalTranslateLoaderFactory, LanguageService } from './app/services/language.service';
import { firstValueFrom } from 'rxjs';

const DEFAULT_LANGUAGE = 'en';
const LANG_CODE_PATTERN = /^[a-z0-9-]{2,16}$/;

function resolveInitialLanguage(): string {
    const saved = (localStorage.getItem('lang') || '').trim().toLowerCase();
    if (LANG_CODE_PATTERN.test(saved)) {
        return saved;
    }

    const browserCandidates = [...(navigator.languages || []), navigator.language];
    for (const candidate of browserCandidates) {
        const code = (candidate || '').split('-')[0].trim().toLowerCase();
        if (LANG_CODE_PATTERN.test(code)) {
            return code;
        }
    }
    return DEFAULT_LANGUAGE;
}

function languageInitializerFactory(translate: TranslateService): () => Promise<void> {
    return async () => {
        const initialLanguage = resolveInitialLanguage();
        translate.setDefaultLang(DEFAULT_LANGUAGE);
        localStorage.setItem('lang', initialLanguage);

        try {
            await firstValueFrom(translate.use(initialLanguage));
        } catch {
            localStorage.setItem('lang', DEFAULT_LANGUAGE);
            await firstValueFrom(translate.use(DEFAULT_LANGUAGE)).catch(() => undefined);
        }
    };
}
const ngxUiLoaderConfig = {
    fgsColor: '#00377b',
    fgsPosition: 'center-center',
    fgsType: 'ball-spin-clockwise',
    fgsSize: 16,
    blur: 3,
    hasProgressBar: false,
    overlayColor: 'rgba(255,255,255,0.5)',
} as NgxUiLoaderConfig;
export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimationsAsync(),
        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled',
            }),
            withEnabledBlockingInitialNavigation()
        ),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: InterceptorService,
            multi: true,
        },
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: EncryptionInterceptor,
        //     multi: true,
        // },
        providePrimeNG({
            theme: {
                preset: CustomTheme,
                options: {
                    darkModeSelector: false || 'none'
                }
            },
            ripple: true,
        }),
        importProvidersFrom(NgxUiLoaderModule.forRoot(ngxUiLoaderConfig)),
        importProvidersFrom(
            TranslateModule.forRoot({
                defaultLanguage: DEFAULT_LANGUAGE,
                loader: {
                    provide: TranslateLoader,
                    useFactory: portalTranslateLoaderFactory,
                    deps: [HttpClient],
                },
            })
        ),
        {
            provide: APP_INITIALIZER,
            useFactory: languageInitializerFactory,
            deps: [TranslateService],
            multi: true,
        },
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        DatePipe,
        AuthService,
        AuthGuard,
        RouteGuard,
        MessageService,
        MessageManagerService,
        NgxUiLoaderService,
        LanguageService,
    ],
};
